/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants';
import utils from '../../utils';

const NOTIFICATION_DURATION_MS = 5000;
const RETRY_LANG_KEY = 'customEval.RETRY';
const SUCCESS_LANG_KEY = 'customEval.SUCCESS';

/** POST /rubrics/:rubricId/custom-eval, scoped to the graph when the tenant has one. */
const buildCustomEvalPath = ({ rubricId, graphId }) => {
  const search = graphId ? `?${new URLSearchParams({ [constants.GRAPH_ID_KEY]: graphId })}` : '';
  return `/rubrics/${encodeURIComponent(String(rubricId))}${constants.CUSTOM_EVAL_PATH_SUFFIX}${search}`;
};

/** First non-empty server message, preferring `message` over `error`. */
const resolveServerMessage = (data) =>
  [data?.message, data?.error].find((value) => typeof value === 'string' && value.trim())?.trim() ?? '';

/** A server message wins; otherwise the translated fallback is shown. */
const notify = ({ type, message, langMessage }) =>
  Framework.getStoreRegistry()
    .getStore('notificationState')
    .set({
      open: true,
      type,
      duration: NOTIFICATION_DURATION_MS,
      ...(message ? { message } : { message: '', langMessage })
    });

const publishOutcome = (outcome) => Framework.getEventManager().publish(constants.SET_CUSTOM_EVAL, outcome);

/**
 * Starts a custom eval from any screen. The outcome arrives as SET_CUSTOM_EVAL with
 * `{ success, rubricId }`, so each screen decides what to do next without this service knowing it.
 * @param {{ tenantId: string, graphId?: string, rubricId: string, requestBody: object }} params
 */
export const requestCustomEval = ({ tenantId, graphId, rubricId, requestBody }) =>
  Framework.getEventManager().publish(constants.CUSTOM_EVAL, { tenantId, graphId, rubricId, requestBody });

/**
 * The single client for custom evals, shared by the Studio evaluation panel and the Rubrics
 * scheduler step. Registered once, in the aiadmin module, which always loads alongside studio.
 */
export default class CustomEvalService extends BaseService {
  constructor() {
    super(constants.CUSTOM_EVAL, 'CustomEvalServiceStatus');
  }

  async handleEvent(eventName, event) {
    const { tenantId, graphId, rubricId, requestBody } = event;
    const headers = utils.buildHeadersWithTenantId({ tenantId });
    const url = buildCustomEvalPath({ rubricId, graphId });
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').post(url, requestBody, { headers });
  }

  handleResponse({ data }) {
    if (data?.success) {
      notify({ type: 'success', message: resolveServerMessage(data), langMessage: SUCCESS_LANG_KEY });
      publishOutcome({ success: true, rubricId: data.rubricId ?? null });
      return;
    }
    notify({ type: 'error', message: resolveServerMessage(data), langMessage: RETRY_LANG_KEY });
    publishOutcome({ success: false });
  }

  handleError(error) {
    publishOutcome({ success: false });
    if (error?.response?.status === 401) {
      utils.redirectToLogin();
      return;
    }
    notify({ type: 'error', message: resolveServerMessage(error?.response?.data), langMessage: RETRY_LANG_KEY });
  }
}
